import PropTypes from "prop-types";
import { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";

import AnnotatedImageViewerDialog from "components/annotations/AnnotatedImageViewerDialog";

const getUrl = (file, baseURL) => {
  if (!file) return null;
  const f = file.data || file;
  const urlPath = f?.attributes?.url || f?.url;
  if (!urlPath) return null;
  if (/^https?:\/\//i.test(urlPath)) return urlPath;
  return `${baseURL || ""}${urlPath}`;
};

export default function MobileImageViewer({ open, onClose, file, baseURL, title, annotations }) {
  const url = useMemo(() => getUrl(file, baseURL), [file, baseURL]);
  const f = useMemo(() => (file?.data ? { id: file.data.id, ...file.data.attributes } : file?.attributes ? { id: file.id, ...file.attributes } : file) || null, [file]);
  const mime = String(f?.mime || "");
  const name = String(f?.name || "Attachment");
  const isPdf = mime === "application/pdf" || name.toLowerCase().endsWith(".pdf");
  const isImage = mime.startsWith("image/");

  if (isImage) {
    return (
      <AnnotatedImageViewerDialog
        open={open}
        title={title || name || "Image preview"}
        imageUrl={url || ""}
        annotations={Array.isArray(annotations) ? annotations : []}
        onClose={onClose}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ position: "relative", height: "100dvh", bgcolor: "#000" }}>
        <Box
          sx={{
            position: "absolute",
            top: "env(safe-area-inset-top)",
            left: 0,
            right: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 1,
          }}
        >
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: "#fff" }}>
            <Icon>close</Icon>
          </IconButton>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", px: 1 }} noWrap>
            {isPdf ? "PDF viewer" : "Pinch to zoom"}
          </Typography>
          <IconButton
            onClick={() => {
              if (!url) return;
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            aria-label="Open in new tab"
            sx={{ color: "#fff" }}
          >
            <Icon>open_in_new</Icon>
          </IconButton>
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: "env(safe-area-inset-top)",
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          {!url ? (
            <Typography variant="body2" sx={{ color: "#fff" }}>
              File unavailable
            </Typography>
          ) : isPdf ? (
            <Box
              component="iframe"
              title={name || "PDF"}
              src={url}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
                bgcolor: "#111",
              }}
            />
          ) : null}
        </Box>
      </Box>
    </Dialog>
  );
}

MobileImageViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  file: PropTypes.any,
  baseURL: PropTypes.string,
  title: PropTypes.string,
  annotations: PropTypes.array,
};

